import * as _ from "lodash";
import * as angular from "angular";
import { CustomFieldService } from "../services/custom-field.service";
import { Field } from "../models/field.model";
import { FieldService } from "../services/field.service";
import { CustomFieldAttachmentModel } from "../models/custom-field-attachment.model";
import {
    fieldTableColumns,
    tableFields,
    types,
} from "../utils/constants/fields.constants";

export class FieldDataProvider {
    public fields: any = null;
    public customFields: any = null;
    public custom: any = null;

    public getFieldsPromise: Promise<any>;
    public getCustomFieldsPromise: Promise<any>;
    public contactFields: Field[] = [];
    public generalFields: Field[] = [];
    public otherFields: Field[] = [];
    public combinedFields: [Field[], Field[], Field[]];
    public isLeaderView: boolean = true;

    // these will hold constants from fields.constants
    public types: any = types;
    public tableFields: any = tableFields;
    public fieldTableColumns: any = fieldTableColumns;

    private $q: any;
    private $timeout: any;
    private service: FieldService;
    private CustomFieldService: CustomFieldService;

    constructor(
        $q: any,
        $timeout: any,
        FieldService: FieldService,
        CustomFieldService: CustomFieldService,
        __app_type: string
    ) {
        this.$timeout = $timeout;
        this.$q = $q;
        this.service = FieldService;
        this.CustomFieldService = CustomFieldService;
        this.isLeaderView = __app_type == "LEADER";
    }

    getTypes() {
        return this.types;
    }

    getAllFields(forceCall: boolean = false): Promise<any> {
        const deferred = this.$q.defer();
        if (this.fields !== null && !forceCall) {
            deferred.resolve(this.fields);
            this.getFieldsPromise = deferred.promise;
            return this.getFieldsPromise;
        } else if (this.getFieldsPromise && !forceCall) {
            return this.getFieldsPromise;
        } else {
            this.service.all().then(
                (response: object) => {
                    this.fields = {
                        priority: [],
                        nonpriority: [],
                        priorityDisabled: [],
                    };
                    this.customFields = [];
                    let json = response["data"];
                    angular.forEach(json.data, (field) => {
                        field = new Field(field);
                        if (field.dragDisabled) {
                            this.fields.priorityDisabled.push(field);
                        } else if (field.priority == "1") {
                            this.fields.priority.push(field);
                        } else {
                            this.fields.nonpriority.push(field);
                        }
                        if (field.cfid) {
                            this.customFields.push(field);
                        }
                    });
                    this.customFields.sort((a, b) => {
                        return a.cfid - b.cfid;
                    });
                    deferred.resolve(this.fields);
                    this.segmentFields();
                },
                (error: object) => {
                    this.fields = null;
                    this.customFields = null;
                    deferred.reject(error);
                }
            );
            this.getFieldsPromise = deferred.promise;
            return this.getFieldsPromise;
        }
    }

    getFieldsDirectory(): Promise<any> {
        const deferred = this.$q.defer();
        this.service.all({ include_hidden: true }).then(
            (response: object) => {
                let json = response["data"];
                let fields: Field[] = [];
                angular.forEach(json.data, (field) => {
                    fields.push(new Field(field));
                });
                deferred.resolve(fields);
            },
            (error: object) => {
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    segmentFields() {
        this.contactFields = [];
        this.generalFields = [];
        this.otherFields = [];

        let allFields = [
            ...this.fields.nonpriority,
            ...this.fields.priority,
            ...this.fields.priorityDisabled,
        ];

        angular.forEach(allFields, (field) => {
            if (
                ["email", "mobilephone", "homephone"].indexOf(field.hokey) > -1
            ) {
                this.contactFields.push(field);
            }
            if (
                [
                    "gender",
                    "employment",
                    "party",
                    "income",
                    "credit_rating",
                ].indexOf(field.hokey) > -1
            ) {
                this.generalFields.push(field);
            }

            if (
                [
                    "dob",
                    "yob",
                    "volunteer",
                    "deceased",
                    "donor",
                    "donor_environmental",
                ].indexOf(field.hokey) > -1
            ) {
                this.otherFields.push(field);
            }
        });
        this.combinedFields = [
            this.contactFields,
            this.generalFields,
            this.otherFields,
        ];
    }

    clearAll(excludeCustomFields?) {
        this.fields = null;
        this.getFieldsPromise = null;
        this.getCustomFieldsPromise = null;
        this.custom = null;
        if (!excludeCustomFields) {
            this.customFields = null;
        }
    }

    getCustomFields(): Promise<any> {
        const deferred = this.$q.defer();
        if (this.customFields !== null) {
            deferred.resolve(this.customFields);
            this.getCustomFieldsPromise = deferred.promise;
            return this.getCustomFieldsPromise;
        } else if (this.getCustomFieldsPromise) {
            return this.getCustomFieldsPromise;
        } else {
            this.fields = null;
            this.getFieldsPromise = null;
            this.getAllFields().then(() => {
                deferred.resolve(this.customFields);
            });
        }
        this.getCustomFieldsPromise = deferred.promise;
        return this.getCustomFieldsPromise;
    }

    getFieldValues(field: string): Promise<any> {
        const deferred = this.$q.defer();
        if (field === "party") {
            this.service.getFieldValues({ id: field }).then((response) => {
                let json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            });
        } else {
            let cfid = field.split("-")[1];
            this.CustomFieldService.getFieldValues({
                id: cfid,
            }).then(
                (response: any): void => {
                    let json = response["data"];
                    if (json.success) {
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error: any): void => deferred.reject(error)
            );
        }

        return deferred.promise;
    }

    updateField(field: Field): Promise<any> {
        const deferred = this.$q.defer();
        let data = _.cloneDeep(field);
        data.id = field.id;
        this.service.update(data).then((response) => {
            let json = response["data"];
            if (json.success) {
                angular.extend(field, json.data.attributes);
                deferred.resolve(field);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    updateMultiple(fields: Field[]): Promise<any> {
        const deferred = this.$q.defer();
        this.service
            .updateMultiple({ fields: fields })
            .then((response: any): void => {
                let json = response["data"];
                if (json.success) {
                    this.getAllFields(true);
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            });
        return deferred.promise;
    }

    getAttachment(data: any): Promise<any> {
        const deferred = this.$q.defer();
        this.service.getAttachment(data).then((response) => {
            let json = response["data"];
            if (json.success) {
                const file = new CustomFieldAttachmentModel(json.data);
                deferred.resolve(file);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    getMultipleAttachments(data: any): Promise<any> {
        const deferred = this.$q.defer();
        this.service.getMultipleAttachments(data).then((response) => {
            let json = response["data"];
            if (json.success) {
                let files = [];
                angular.forEach(json.data, (file) => {
                    files.push(new CustomFieldAttachmentModel(file));
                });
                deferred.resolve(files);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    updateOrder(data: any): Promise<any> {
        const deferred = this.$q.defer();
        this.service.updateOrder(data).then((json) => {
            deferred.resolve(data);
            this.clearAll(true);
        });
        return deferred.promise;
    }

    setFields(fields: Field[]): void {
        this.fields = fields;
    }

    updateCustomField(customField: any): Promise<any> {
        const deferred = this.$q.defer();
        if (customField.cftid == 4 && customField.checkboxes)
            customField.cftid = 5;
        this.CustomFieldService.update(customField).then((response) => {
            let json = response["data"];
            if (json.success) {
                angular.extend(customField, json.data);
                deferred.resolve(customField);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    createField(customField: any): Promise<any> {
        const deferred = this.$q.defer();
        if (customField.cftid == 4 && customField.checkboxes)
            customField.cftid = 5;
        this.CustomFieldService.create(customField).then(
            (response) => {
                let json = response["data"];
                if (json.success) {
                    let newField = new Field(json.data);
                    this.replaceCustomField(newField);
                    deferred.resolve(newField);
                } else {
                    deferred.reject(json);
                }
            },
            (error) => {
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    deleteField(field: Field): Promise<any> {
        const deferred = this.$q.defer();
        this.CustomFieldService.delete({ id: field.cfid }).then((response) => {
            let json = response["data"];
            if (json.success) {
                this.spliceField(field);
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    spliceField(field: Field): void {
        angular.forEach(this.fields, (fieldsArray, arrayName) => {
            angular.forEach(fieldsArray, (thisField, $index) => {
                if (thisField.id == field.id) {
                    this.fields[arrayName].splice($index, 1);
                }
            });
        });
        angular.forEach(this.customFields, (customField, $index) => {
            if (customField.id == field.id) {
                this.customFields.splice($index, 1);
            }
        });
    }

    fieldWithID(id: number): Field | null {
        let foundField = null;
        angular.forEach(this.fields, (fieldsArray) => {
            angular.forEach(fieldsArray, (thisField) => {
                if (thisField.id == id) {
                    foundField = thisField;
                }
            });
        });
        return foundField;
    }

    replaceCustomField(data: any): void {
        this.$timeout(() => {
            let existingField = this.fieldWithID(data.id);
            if (existingField) {
                angular.extend(existingField, data);
            } else {
                let newField = new Field(data);
                this.fields.nonpriority.unshift(newField);

                if (this.customFields) {
                    this.customFields.unshift(newField);
                }
            }
        });
    }

    rewriteRegularFields(fields: any[]): Promise<any> {
        const deferred = this.$q.defer();
        this.custom = [];
        angular.forEach(fields, (field, key) => {
            if (field.cfid == null) {
                if (
                    ![
                        "income",
                        "credit_rating",
                        "donor_environmental",
                    ].includes(field.hokey)
                ) {
                    field.hokey = "ho" + field.hokey;
                }
                this.custom.push(field);
            } else {
                this.custom.push(field);
            }
            if (fields.length - 1 === key) {
                deferred.resolve(this.custom);
            }
        });
        return deferred.promise;
    }

    processSocketUpdate(socketData: any): void {
        if (this.fields) {
            this.replaceCustomField(socketData);
        }
    }

    processSocketCreate(socketData: any): void {
        if (this.fields) {
            this.replaceCustomField(socketData);
        }
    }

    processSocketDelete(socketData: any): void {
        if (this.fields) {
            let existingField = this.fieldWithID(socketData.id);
            if (existingField) {
                this.spliceField(existingField);
            }
        }
    }

    formatFields(customfields: any): any[] {
        angular.forEach(customfields, (value) => {
            if (value.hokey) {
                if (value.hokey == "employment") {
                    value.hokey = "esname";
                } else if (
                    ![
                        "income",
                        "credit_rating",
                        "donor_environmental",
                    ].includes(value.hokey)
                ) {
                    value.hokey = "ho" + value.hokey;
                }
            }
        });
        return customfields;
    }

    matchDynamicFieldName(fields: any, objWithHoKey: any): string | null {
        if (!fields) {
            return null;
        }
        let matchedField = [
            ...fields.nonpriority,
            ...fields.priority,
            ...fields.priorityDisabled,
        ].filter((prop) => prop.hokey == objWithHoKey.hokey);
        if (matchedField && matchedField[0]) {
            return matchedField[0].name;
        }
        return null;
    }
    matchColumnDisplayNameWithDynamicfieldName(column: any): void {
        column.columnname =
            this.matchDynamicFieldName(this.fields, column) ||
            column.columnname;
    }

    columnDisplayName(column: any): string | null {
        if (column.hokey) {
            this.matchColumnDisplayNameWithDynamicfieldName(column);
        }
        if (column.elname != null) {
            return column.elname;
        } else if (column.columnname != null) {
            return column.columnname;
        } else if (column.cfname != null) {
            return column.cfname;
        } else if (column.name != null) {
            return column.name;
        }
        return null;
    }
}

angular
    .module("vcDataProvider")
    .service("FieldDataProvider", FieldDataProvider);
